import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Link,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  AutoAwesome as MagicIcon,
  Send as SendIcon
} from '@mui/icons-material';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]); // { role: 'user' | 'assistant', content: string }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiInputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

  const renderMessageContent = (content) => {
    if (!content) return null;
    const blocks = content.split(/```/);
    return blocks.map((block, blockIndex) => {
      const isCodeBlock = blockIndex % 2 === 1;
      if (isCodeBlock) {
        return (
          <Box
            key={`code-${blockIndex}`}
            component="pre"
            sx={{
              mt: 1,
              mb: 1,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: 'rgba(15,23,42,0.9)',
              overflowX: 'auto',
              fontFamily: 'SFMono-Regular,Menlo,Monaco,Consolas,\"Liberation Mono\",\"Courier New\",monospace',
              fontSize: 12.5,
              border: '1px solid rgba(55,65,81,0.8)'
            }}
          >
            <code>{block.trim()}</code>
          </Box>
        );
      }

      const lines = block.split(/(\n)/);
      return lines.map((line, lineIndex) => {
        if (line === '\n') {
          return <br key={`br-${blockIndex}-${lineIndex}`} />;
        }
        const parts = line.split(/(\s+)/);
        return (
          <React.Fragment key={`line-${blockIndex}-${lineIndex}`}>
            {parts.map((part, index) => {
              if (part.startsWith('http://') || part.startsWith('https://')) {
                return (
                  <Link
                    key={`link-${blockIndex}-${lineIndex}-${index}`}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: '#93c5fd' }}
                  >
                    {part}
                  </Link>
                );
              }

              if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
                const codeText = part.slice(1, -1);
                return (
                  <Box
                    key={`code-inline-${blockIndex}-${lineIndex}-${index}`}
                    component="span"
                    sx={{
                      fontFamily:
                        'SFMono-Regular,Menlo,Monaco,Consolas,\"Liberation Mono\",\"Courier New\",monospace',
                      fontSize: '0.85em',
                      backgroundColor: 'rgba(15,23,42,0.9)',
                      borderRadius: 0.75,
                      px: 0.5,
                      py: 0.1
                    }}
                  >
                    {codeText}
                  </Box>
                );
              }

              return part;
            })}
          </React.Fragment>
        );
      });
    });
  };

  const aiSuggestedQuestions = [
    'Give me a summary of all agents and their status.',
    'How many agents are disconnected right now?',
    'Break down agents by operating system.',
    'List any agents with outdated versions.',
    'Is there anything unusual about my agents today?'
  ];

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    // Handle URL parameters for filtering
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      switch (filterParam) {
        case 'active':
          setStatusFilter('active');
          break;
        case 'inactive':
        case 'disconnected':
          setStatusFilter('disconnected');
          break;
        case 'total':
        case 'all':
          setStatusFilter('all');
          break;
        default:
          setStatusFilter('all');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    let filtered = agents;

    if (searchTerm) {
      filtered = filtered.filter(agent =>
        agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.ip.includes(searchTerm) ||
        agent.id.includes(searchTerm) ||
        agent.os.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(agent => agent.status === statusFilter);
    }

    if (agentFilter !== 'all') {
      filtered = filtered.filter(agent => agent.id === agentFilter);
    }

    setFilteredAgents(filtered);
  }, [agents, searchTerm, statusFilter, agentFilter]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const agentsData = await dashboardApiService.getAllAgents();
      setAgents(agentsData);
    } catch (err) {
      setError('Failed to fetch agents data');
      console.error('Agents fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'disconnected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="status-icon-active" />;
      case 'disconnected':
        return <CancelIcon className="status-icon-disconnected" />;
      default:
        return <InfoIcon className="status-icon-default" />;
    }
  };

  const formatLastKeepAlive = (lastKeepAlive) => {
    if (lastKeepAlive === '9999-12-31T23:59:59+00:00') {
      return 'Never disconnected';
    }
    return new Date(lastKeepAlive).toLocaleString();
  };

  const handleAgentSelect = (agent) => {
    navigate(`/agents/${agent.id}`);
  };

  const getOSIcon = (osName) => {
    return <ComputerIcon className="os-icon" />;
  };

  const handleOpenAiDialog = () => {
    setAiDialogOpen(true);
    setAiError(null);
  };

  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
    setAiMessages([]);
    setAiError(null);
  };

  const buildAgentsContext = () => {
    try {
      const summary = agents.map((agent) => ({
        id: agent.id,
        name: agent.name,
        status: agent.status,
        ip: agent.ip,
        os: agent.os,
        version: agent.version,
        manager: agent.manager
      }));
      return JSON.stringify(summary);
    } catch (e) {
      return 'No agent context available.';
    }
  };

  const handleSendAiPrompt = async () => {
    const prompt = aiInputRef.current ? aiInputRef.current.value : '';
    if (!prompt.trim()) {
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiMessages((prev) => [...prev, { role: 'user', content: prompt }]);
    if (aiInputRef.current) {
      aiInputRef.current.value = '';
    }

    const context = buildAgentsContext();

    try {
      const token = localStorage.getItem('cognito_token');
      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `You are an assistant helping with questions about agents. Agent data (JSON array): ${context}`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 200
        })
      });

      if (!response.ok) {
        let extra = '';
        try {
          const errText = await response.text();
          extra = errText ? ` - ${errText}` : '';
        } catch (e) {
          // ignore parsing error
        }
        throw new Error(`Request failed with status ${response.status}${extra}`);
      }

      const data = await response.json();
      const content = data?.content || 'No response received.';
      setAiMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch (err) {
      console.error('AI assistant error:', err);
      setAiError(err.message || 'Failed to get response from AI assistant');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <Box className="agents-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">Loading agents...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="agents-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchAgents}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="agents" sx={{ position: 'relative', minHeight: '100%' }}>
      <Box className="agents-header">
        <Typography variant="h4" className="agents-title">
          Agents Management
        </Typography>
        <Typography variant="body1" className="agents-subtitle">
          Monitor and manage all OpenShield agents
        </Typography>
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 2,
          borderRadius: 2,
          border: '1px solid rgba(147,197,253,0.45)',
          background: 'linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)'
        }}
      >
        <Typography variant="body2">
          Agents must be deployed using the <strong>token-based Deploy Agent command</strong>. Once registered, an agent
          remains connected as long as it is running and communicating with the manager.
        </Typography>
      </Alert>

      {/* Filters and Controls */}
      <Box className="agents-controls">
        <Box className="agents-filters">
          <FormControl className="filter-dropdown">
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status Filter"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disconnected">Disconnected</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl className="filter-dropdown">
            <InputLabel>Agent Filter</InputLabel>
            <Select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              label="Agent Filter"
            >
              <MenuItem value="all">All Agents</MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name} (ID: {agent.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="agents-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Box className="agents-stats">
          <Chip 
            label={`Total: ${agents.length}`} 
            className="stat-chip total"
          />
          <Chip 
            label={`Active: ${agents.filter(a => a.status === 'active').length}`} 
            className="stat-chip active"
          />
          <Chip 
            label={`Disconnected: ${agents.filter(a => a.status === 'disconnected').length}`} 
            className="stat-chip disconnected"
          />
          <Chip 
            label={`Filtered: ${filteredAgents.length}`} 
            className="stat-chip filtered"
          />
        </Box>
      </Box>

      {/* Agents Table View */}
      <TableContainer component={Paper} className="agents-table-container">
        <Table className="agents-table">
          <TableHead>
            <TableRow className="table-header">
              <TableCell className="table-header-cell">Agent Name</TableCell>
              <TableCell className="table-header-cell">Status</TableCell>
              <TableCell className="table-header-cell">IP Address</TableCell>
              <TableCell className="table-header-cell">Operating System</TableCell>
              <TableCell className="table-header-cell">Version</TableCell>
              <TableCell className="table-header-cell">Last Keep Alive</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgents.map((agent) => (
              <TableRow 
                key={agent.id}
                className="table-row"
                onClick={() => handleAgentSelect(agent)}
              >
                <TableCell className="table-cell agent-name-cell">
                  <Box className="agent-name-container">
                    <Avatar className="table-agent-avatar">
                      {getOSIcon(agent.os.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" className="table-agent-name">
                        {agent.name}
                      </Typography>
                      <Typography variant="caption" className="table-agent-id">
                        ID: {agent.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="status-container">
                    {getStatusIcon(agent.status)}
                    <Chip 
                      label={agent.status}
                      color={getStatusColor(agent.status)}
                      size="small"
                      className="table-status-chip"
                    />
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-ip">
                    {agent.ip}
                  </Typography>
                  <Typography variant="caption" className="table-register-ip">
                    Register: {agent.registerIP}
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-os">
                    {agent.os.name} {agent.os.version}
                  </Typography>
                  <Typography variant="caption" className="table-platform">
                    {agent.os.platform} ({agent.os.arch})
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-version">
                    {agent.version}
                  </Typography>
                  <Typography variant="caption" className="table-manager">
                    Manager: {agent.manager}
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Typography variant="body2" className="table-last-seen">
                    {formatLastKeepAlive(agent.lastKeepAlive)}
                  </Typography>
                  {agent.disconnection_time && (
                    <Typography variant="caption" className="table-disconnection">
                      Disconnected: {new Date(agent.disconnection_time).toLocaleString()}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {filteredAgents.length === 0 && !loading && (
        <Box className="no-agents">
          <Typography variant="h6">No agents found</Typography>
          <Typography variant="body2">
            {searchTerm ? 'Try adjusting your search terms' : 'No agents are currently registered'}
          </Typography>
        </Box>
      )}

      {/* Floating AI Assistant FAB */}
      <Tooltip title="Ask AI about your agents">
        <span>
          <Fab
            color="primary"
            onClick={handleOpenAiDialog}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1200,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 12px 30px rgba(102,126,234,0.5)'
            }}
          >
            <MagicIcon />
          </Fab>
        </span>
      </Tooltip>
      {/* AI Assistant Dialog */}
      <Dialog
        open={aiDialogOpen}
        onClose={handleCloseAiDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#020617',
            color: '#e5e7eb',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(148,163,184,0.35)', bgcolor: 'transparent' }}>
          Agents AI Assistant
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 360 }}>
            <Paper
              sx={{
                flex: 1,
                p: 2.5,
                backgroundColor: '#020617',
                borderRadius: 2,
                overflowY: 'auto',
                border: '1px solid rgba(30,64,175,0.6)'
              }}
            >
              {aiMessages.length === 0 && !aiLoading && !aiError && (
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    backgroundColor: 'rgba(30,64,175,0.35)',
                    boxShadow: '0 18px 45px rgba(15,23,42,0.8)',
                    maxWidth: '80%'
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                    Welcome! Ask anything about your agents, for example: "Which agents are disconnected?" or "Summarize active agents by OS".
                  </Typography>
                </Box>
              )}
              {aiMessages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {msg.role === 'assistant' && (
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        mr: 1.2,
                        bgcolor: 'rgba(37,99,235,0.9)',
                        fontSize: 14
                      }}
                    >
                      AI
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: '75%',
                      px: 1.8,
                      py: 1.1,
                      borderRadius: 2.5,
                      backgroundColor: msg.role === 'user' ? 'rgba(59,130,246,0.95)' : 'rgba(15,23,42,0.95)',
                      color: '#e5e7eb',
                      boxShadow: msg.role === 'user'
                        ? '0 18px 40px rgba(37,99,235,0.55)'
                        : '0 14px 32px rgba(15,23,42,0.9)',
                      border: msg.role === 'user'
                        ? '1px solid rgba(129,140,248,0.9)'
                        : '1px solid rgba(31,41,55,0.9)'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.75, display: 'block', mb: 0.3 }}
                    >
                      {msg.role === 'user' ? 'You' : 'Assistant'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 13.5 }}
                    >
                      {renderMessageContent(msg.content)}
                    </Typography>
                  </Box>
                  {msg.role === 'user' && (
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        ml: 1.2,
                        bgcolor: 'rgba(148,163,184,0.9)',
                        fontSize: 14
                      }}
                    >
                      You
                    </Avatar>
                  )}
                </Box>
              ))}
              {aiLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Thinking...</Typography>
                </Box>
              )}
              {aiError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {aiError}
                </Alert>
              )}
            </Paper>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: -0.5 }}>
              {aiSuggestedQuestions.map((q) => (
                <Button
                  key={q}
                  size="small"
                  variant="outlined"
                  disabled={aiLoading}
                  onClick={() => {
                    if (aiInputRef.current) {
                      aiInputRef.current.value = q;
                    }
                    handleSendAiPrompt();
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    borderColor: 'rgba(148,163,184,0.6)',
                    color: '#e5e7eb',
                    fontSize: 12,
                    px: 1.5,
                    py: 0.25
                  }}
                >
                  {q}
                </Button>
              ))}
            </Box>

            <TextField
              placeholder="Ask a question about your agents..."
              fullWidth
              inputRef={aiInputRef}
              size="small"
              InputProps={{
                sx: {
                  borderRadius: 999,
                  backgroundColor: 'rgba(15,23,42,0.95)',
                  px: 1.5,
                  color: '#e5e7eb',
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(148,163,184,0.9)'
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleSendAiPrompt}
                      disabled={aiLoading}
                      sx={{
                        minWidth: 0,
                        p: 0.5,
                        borderRadius: '50%',
                        color: '#fde68a'
                      }}
                    >
                      <SendIcon />
                    </Button>
                  </InputAdornment>
                )
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!aiLoading) {
                    handleSendAiPrompt();
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAiDialog}>Close</Button>
          <Button onClick={handleSendAiPrompt} variant="contained" disabled={aiLoading}>
            {aiLoading ? 'Asking...' : 'Ask AI'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agents;
